import type { ComponentPropsWithoutRef } from 'react';
import styled from '@emotion/styled';
import { hexToRgb } from 'src/utils/common';
import { hoverAndActiveCss } from 'src/utils/styled';
type ButtonSize = 'large' | 'medium' | 'small';
type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'outline' | 'blur' | 'brightFixed' | 'accent' | 'textPrimary' | 'textSecondary';
const RootStyle = styled.button<{
  size: ButtonSize;
  variant: ButtonVariant;
}>`
  box-sizing: border-box;

  cursor: pointer;
  outline: none;
  background: none;
  border: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  border-width: 1px;
  border-style: solid;
  border-radius: 20px;

  font-size: 14px;
  font-weight: 700;
  line-height: 16px;

  ${({
  size,
  variant
}) => ({
  large: `
        height: ${variant.startsWith('text') ? '24px' : '52px'};
        padding: 0 15px;
        border-radius: 99px;

        font-size: 16px;
        line-height: 16px;

        > span {
          padding: 0 6px;
        }

        > svg,
        > img {
          width: 20px;
          height: 20px;
        }
      `,
  medium: `
        height: 40px;
        padding: 0 15px;

        > span {
          padding: 0 4px;
        }

        > svg,
        > img {
          width: 16px;
          height: 16px;
        }
      `,
  small: `
        height: 32px;
        padding: 0 11px;

        > span {
          padding: 0 4px;
        }

        > svg,
        > img {
          width: 16px;
          height: 16px;
        }
      `
})[size]}
`;
const ButtonVariantMap: Record<ButtonVariant, typeof RootStyle> = {
  primary: styled(RootStyle)`
    background-color: ${({
    theme
  }) => theme.color.primary1};
    color: ${({
    theme
  }) => theme.color.contentInverse};
    border-color: transparent;

    ${({
    theme
  }) => hoverAndActiveCss(`
        background-color: ${theme.color.primary2};
      `)}

    :disabled {
      background-color: ${({
    theme
  }) => theme.color.surface4};
      color: ${({
    theme
  }) => theme.color.content6};
    }
  `,
  secondary: styled(RootStyle)`
    background-color: ${({
    theme
  }) => theme.color.surface12};
    color: ${({
    theme
  }) => theme.color.contentBright};
    border-color: transparent;

    ${({
    theme
  }) => hoverAndActiveCss(`
        background-color: ${theme.color.surface11};
      `)}

    :disabled {
      background-color: ${({
    theme
  }) => theme.color.surface4};
      color: ${({
    theme
  }) => theme.color.content6};
    }
  `,
  tertiary: styled(RootStyle)`
    background-color: ${({
    theme
  }) => theme.color.surface4};
    color: ${({
    theme
  }) => theme.color.content11};
    border-color: transparent;

    ${({
    theme
  }) => hoverAndActiveCss(`
        background-color: ${theme.color.surface6};
      `)}

    :disabled {
      color: ${({
    theme
  }) => theme.color.content6};
    }
  `,
  quaternary: styled(RootStyle)`
    background-color: ${({
    theme
  }) => theme.color.surfaceBright};
    color: ${({
    theme
  }) => theme.color.contentDeep};
    border-color: transparent;

    ${({
    theme
  }) => hoverAndActiveCss(`
        background-color: ${theme.color.surface5};
      `)}

    :disabled {
      background-color: ${({
    theme
  }) => theme.color.surface4};
      color: ${({
    theme
  }) => theme.color.content6};
    }
  `,
  outline: styled(RootStyle)`
    background-color: transparent;
    color: ${({
    theme
  }) => theme.color.content11};
    border-color: ${({
    theme
  }) => theme.color.surface5};

    ${({
    theme
  }) => hoverAndActiveCss(`
        background-color: ${theme.color.surface3};
      `)}

    :disabled {
      color: ${({
    theme
  }) => theme.color.content6};
    }
  `,
  blur: styled(RootStyle)`
    background-color: rgba(${({
    theme
  }) => hexToRgb(theme.color.surfaceBrightFixed)}, 0.3);
    color: ${({
    theme
  }) => theme.color.contentBrightFixed};
    border-color: transparent;
    backdrop-filter: blur(10px);

    ${({
    theme
  }) => hoverAndActiveCss(`background-color: rgba(${hexToRgb(theme.color.surfaceBrightFixed)}, 0.6);`)}

    :disabled {
      background-color: rgba(${({
    theme
  }) => hexToRgb(theme.color.surfaceBrightFixed)}, 0.1);
      color: ${({
    theme
  }) => theme.color.contentBrightFixed};

      opacity: 0.5;
    }
  `,
  brightFixed: styled(RootStyle)`
    background-color: ${({
    theme
  }) => theme.color.surfaceBrightFixed};
    color: ${({
    theme
  }) => theme.color.contentDeepFixed};
    border-color: transparent;

    ${({
    theme
  }) => hoverAndActiveCss(`
        background-color: ${theme.color.surface4};
      `)}

    :disabled {
      background-color: ${({
    theme
  }) => theme.color.surfaceBrightFixed};
      color: ${({
    theme
  }) => theme.color.content6Fixed};
    }
  `,
  accent: styled(RootStyle)`
    background-color: ${({
    theme
  }) => theme.color.accentA1};
    color: ${({
    theme
  }) => theme.color.contentBrightFixed};
    border-color: transparent;

    ${({
    theme
  }) => hoverAndActiveCss(`
        background-color: ${theme.color.accentA2};
      `)}

    :disabled {
      color: ${({
    theme
  }) => theme.color.surface4};
    }
  `,
  textPrimary: styled(RootStyle)`
    background-color: transparent;
    color: ${({
    theme
  }) => theme.color.contentBrightFixed};
    border-color: transparent;

    ${({
    theme
  }) => hoverAndActiveCss(`
        color: rgba(${hexToRgb(theme.color.contentBrightFixed)}, 0.5);
      `)}

    :disabled {
      color: rgba(${({
    theme
  }) => hexToRgb(theme.color.contentBrightFixed)}, 0.3);
    }
  `,
  textSecondary: styled(RootStyle)`
    background-color: transparent;
    color: ${({
    theme
  }) => theme.color.content8};
    border-color: transparent;

    ${({
    theme
  }) => hoverAndActiveCss(`
        color: ${theme.color.content9};
      `)}

    :disabled {
      color: ${({
    theme
  }) => theme.color.content6};
    }
  `
};
type PropsBase = {
  variant: Extract<ButtonVariant, 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'accent' | 'outline' | 'blur' | 'brightFixed' | 'textPrimary' | 'textSecondary'>;
  size: Extract<ButtonSize, 'large'>;
} | {
  variant: Extract<ButtonVariant, 'primary' | 'secondary' | 'tertiary' | 'outline' | 'blur'>;
  size: Extract<ButtonSize, 'medium' | 'small'>;
};
export type GenericButtonProps = ComponentPropsWithoutRef<'button'> & PropsBase;
const GenericButton = ({
  type = 'button',
  size,
  variant,
  children,
  ...props
}: GenericButtonProps) => {
  const Component = ButtonVariantMap[variant];
  return <Component {...props} type={type} size={size} variant={variant} data-sentry-element="Component" data-sentry-component="GenericButton" data-sentry-source-file="index.tsx">
      {typeof children === 'string' ? <span>{children}</span> : children}
    </Component>;
};
export default GenericButton;